import { Entity } from '@backstage/catalog-model';
import { TableColumn } from '@backstage/core-components';
import { CatalogTableRow } from '@backstage/plugin-catalog';
import { Chip, makeStyles } from '@material-ui/core';
import { useEffect } from 'react';

import {
  FRAMEWORK_IDS,
  FrameworkId,
} from '@internal/backstage-plugin-framework-versions-common';

import {
  EolData,
  FrameworkRow,
  evaluateFrameworks,
  useFrameworkStatuses,
} from '../hooks/useFrameworkStatuses';
import { FRAMEWORKS, readFrameworks } from '../lib/frameworks';
import { FrameworkStatus, STATUS_TEXT, worstStatus } from '../lib/status';

const useStyles = makeStyles(theme => ({
  chips: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(0.5) },
  supported: { borderColor: theme.palette.status.ok },
  'nearing-eol': { borderColor: theme.palette.status.warning },
  'below-standard': { borderColor: theme.palette.status.warning },
  unsupported: { borderColor: theme.palette.status.error },
  unknown: { borderColor: theme.palette.status.aborted },
}));

const groupById = (rows: FrameworkRow[]) => {
  const groups = new Map<FrameworkId, FrameworkRow[]>();
  rows.forEach(row => groups.set(row.id, [...(groups.get(row.id) ?? []), row]));
  return [...groups];
};

const SORT_ORDER: FrameworkStatus[] = [
  'unsupported',
  'below-standard',
  'nearing-eol',
  'supported',
  'unknown',
];

const FrameworkChips = ({
  entity,
  sortData,
}: {
  entity: Entity;
  sortData: EolData;
}) => {
  const classes = useStyles();
  // The table only renders one page, but sorts every row, so load the
  // end-of-life data for all frameworks.
  const { rows, loading, eolData } = useFrameworkStatuses(
    entity,
    FRAMEWORK_IDS,
  );
  useEffect(() => {
    if (eolData.releases) Object.assign(sortData, eolData);
  }, [eolData, sortData]);

  return (
    <div className={classes.chips}>
      {groupById(rows).map(([id, versions]) => {
        const status = worstStatus(versions.map(v => v.status));
        const name = `${FRAMEWORKS[id].title} ${versions
          .map(v => v.version)
          .join(', ')}`;
        if (loading) {
          return <Chip key={id} size="small" variant="outlined" label={name} />;
        }
        return (
          <Chip
            key={id}
            size="small"
            variant="outlined"
            className={classes[status]}
            label={`${name} · ${STATUS_TEXT[status]}`}
            title={versions
              .map(v => `${v.version}: ${STATUS_TEXT[v.status]}`)
              .join(', ')}
          />
        );
      })}
    </div>
  );
};

const searchText = (entity: Entity) =>
  readFrameworks(entity).flatMap(({ id, version }) =>
    [FRAMEWORKS[id].title, id].map(name => `${name} ${version}`.toLowerCase()),
  );

export const createFrameworksColumn = (): TableColumn<CatalogTableRow> => {
  const sortData: EolData = { minimums: {} };
  const rank = (entity: Entity) => {
    const rows = evaluateFrameworks(readFrameworks(entity), sortData);
    if (!rows.length) return SORT_ORDER.length;
    return SORT_ORDER.indexOf(worstStatus(rows.map(r => r.status)));
  };

  return {
    title: 'Frameworks',
    customSort: (a, b) => rank(a.entity) - rank(b.entity),
    render: row => <FrameworkChips entity={row.entity} sortData={sortData} />,
    customFilterAndSearch: (filter, row) => {
      const term = filter.toLowerCase();
      return searchText(row.entity).some(text => text.includes(term));
    },
  };
};
